import React, { useEffect, useState } from 'react';
import { Grid, List, ListItemText, ListItemSecondaryAction, ListItem } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import IconButton from '@material-ui/core/IconButton';
import Close from '@mui/icons-material/Close';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { format } from 'date-fns';
import { CircularProgress } from '@mui/material';
import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';

import config from 'config';
import FormTextFieldRedesign from 'retired/FormTextFieldRedesign';
import { purple } from 'utils/color-styles';
import { useMinActionsQuery } from 'generated/graphql';
import { MinAction } from 'shared/types';
import { ActionChip } from 'containers/DeedsPage/ActionChip';
import makeSortableItem from 'retired/SortableItem';

const ActionListItem = ({ action, onRemove }: { action: MinAction; onRemove: () => void }) => (
  <div
    style={{
      width: '100%',
      cursor: 'move',
      position: 'relative',
      pointerEvents: 'none',
    }}
  >
    <div
      style={{
        position: 'absolute',
        width: '100%',
        textAlign: 'center',
        top: 8,
      }}
    >
      <DragHandleIcon />
    </div>
    <ListItem
      key={action.id}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.03)',
        borderRadius: 16,
        marginBottom: 8,
      }}
    >
      <ListItemText
        primary={
          <a
            href={`${config.clientUrl}/action/${action.id}`} // actions form
            target="_blank"
            style={{ cursor: 'pointer', pointerEvents: 'initial', color: purple }}
          >
            {action.title}
          </a>
        }
        secondary={
          <>
            Start date:{' '}
            {action.startingAt
              ? format(legacyParse(action.startingAt), convertTokens('MMM DD, YYYY'))
              : 'Not set'}
          </>
        }
      />

      <ListItemSecondaryAction style={{ display: 'flex', alignItems: 'center' }}>
        <ActionChip status={action.status} />
        <IconButton
          edge="end"
          aria-label="delete"
          onClick={onRemove}
          style={{ pointerEvents: 'initial', marginLeft: 50 }}
        >
          <Close />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  </div>
);

const SortableActionListItem = makeSortableItem({
  itemType: 'action',
  wrapperStyle: {
    position: 'relative',
    maxWidth: 1000,
    paddingTop: 6,
    paddingBottom: 2,
    padding: 0,
    marginBottom: 10,
  },
})(ActionListItem);

export const AddActionsSection = ({
  campaignId,
  selectedActions,
  setSelectedActions,
  actionsSorting,
}: {
  campaignId?: string;
  selectedActions: MinAction[];
  setSelectedActions: (actions: MinAction[]) => void;
  actionsSorting: string[];
}) => {
  const [contentSelectorTouched, setContentSelectorTouched] = useState(false);
  const [options, setOptions] = useState<MinAction[]>([]);
  const [removedExistingActions, setRemovedExistingActions] = useState<MinAction[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const {
    data: existingActionsData,
    error: existingActionsError,
    loading: existingActionsLoading,
  } = useMinActionsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      where: {
        campaignIds: { has: campaignId },
      },
    },
    skip: !campaignId,
  });

  const existingActions = existingActionsData?.actionsWithPagination?.items;

  const {
    data: allActionsData,
    error: allActionsError,
    loading: allActionsLoading,
  } = useMinActionsQuery();

  const allActions = allActionsData?.actionsWithPagination?.items ?? [];

  useEffect(() => {
    if (!allActionsLoading && !allActionsError) {
      setOptions(
        allActions?.filter(
          (action) =>
            !selectedActions.find((selectedAction) => selectedAction.id === action.id) &&
            action.title.toLowerCase().includes(searchTerm.toLowerCase())
        ) || []
      );
    }
  }, [allActionsLoading, searchTerm, selectedActions]);

  useEffect(() => {
    if (!existingActionsLoading && !existingActionsError) {
      const sortedActions = existingActions?.sort(
        (a, b) => actionsSorting.indexOf(a.id) - actionsSorting.indexOf(b.id)
      );
      setSelectedActions(sortedActions || []);
    }
  }, [existingActionsLoading]);

  const addNew = (newAction: MinAction): void => {
    setContentSelectorTouched(true);
    setSelectedActions([...selectedActions, newAction]);
  };

  const isActionInOtherCampaign = (action: MinAction) =>
    !!action.campaignIds.length &&
    !removedExistingActions.find((removedExistingAction) => removedExistingAction.id === action.id);

  const moveAction = (dragIndex: number, hoverIndex: number) => {
    const newActions = [...selectedActions];
    const draggedAction = newActions[dragIndex];

    newActions.splice(dragIndex, 1);
    newActions.splice(hoverIndex, 0, draggedAction);

    setSelectedActions(newActions);
  };

  const removeAction = (action: MinAction) => {
    setSelectedActions(selectedActions.filter((a) => a.id !== action.id));

    if (
      existingActions?.find((existingAction) => existingAction.id === action.id) &&
      !removedExistingActions.find((removedAction) => removedAction.id === action.id)
    ) {
      setRemovedExistingActions([...removedExistingActions, action]);
    }
  };

  if (existingActionsLoading) {
    return <CircularProgress />;
  }

  return (
    <>
      <Grid container spacing={3}>
        <Grid item md={6}>
          <Autocomplete
            disableClearable
            onChange={(_event, content): void => {
              addNew(content);
              setSearchTerm('');
            }}
            getOptionLabel={(option) => option.title}
            loading={allActionsLoading}
            placeholder="Search by title"
            inputValue={searchTerm}
            getOptionDisabled={(option) => isActionInOtherCampaign(option)}
            renderInput={(params): JSX.Element => (
              <FormTextFieldRedesign
                {...params}
                label="Search Actions"
                onBlur={(): void => setContentSelectorTouched(true)}
                onFocus={(): void => setContentSelectorTouched(false)}
                onChange={(e): void => setSearchTerm(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                alwaysDisplayErrors={contentSelectorTouched}
              />
            )}
            renderOption={(option): JSX.Element => (
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ color: purple }}>{option.title}</div>
                {isActionInOtherCampaign(option) ? (
                  <div style={{ paddingRight: 10, fontSize: 10 }}>
                    Already part of another campaign
                  </div>
                ) : null}
              </div>
            )}
            options={options ?? []}
            noOptionsText="No content found"
            // Workaround to fix the @material-ui Autocomplete known issue: https://stackoverflow.com/questions/60409071/react-material-ui-autocomplete-show-options-with-issue
            filterOptions={(option) => option}
          />
          <p style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.54)' }}>
            Search for the Actions you&apos;d like to find by the Action title
          </p>
        </Grid>
      </Grid>

      <List style={{ maxHeight: 560, overflow: 'auto' }}>
        {selectedActions.map((action, index: number) => (
          <SortableActionListItem
            key={action.id}
            index={index}
            move={moveAction}
            action={action}
            onRemove={() => removeAction(action)}
          />
        ))}
      </List>
    </>
  );
};
